import React from 'react';
import { selectInputValueOnFocus } from './generationProfileInputsUtils';

const dirtyInputClass = 'border-red-400 focus:border-red-500 focus:ring-red-200';
const DECIMAL_2_REGEX = /^\d*(\.\d{0,2})?$/;

/**
 * Nested tech rows under a fuel bucket (VO mode).
 * Capacity mode can be editable (sub-fuel GW for simulation).
 */
export default function VoFuelTechSublist({
  rows,
  mode = 'capacity',
  editable = false,
  draftByTechId = null,
  dirtyTechIds = null,
  onChangeTech,
  onEnter,
}) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <ul className="mt-1 mb-1 ml-6 pl-2 border-l border-gray-200 space-y-1">
      {rows.map((row) => {
        const key = `${row.techId || row.tech}|${row.techByFuel ?? ''}|${row.label}`;
        if (mode === 'generation') {
          const pct = ((row.utilization || 0) * 100).toFixed(1);
          const twh = Number.isFinite(row.generationTWh) ? row.generationTWh.toFixed(2) : '—';
          return (
            <li
              key={key}
              className="flex items-center justify-between gap-2 text-[11px] text-gray-600"
              title={row.lastResort ? 'Last-resort flex (zero model gen / no cost)' : undefined}
            >
              <span className="truncate min-w-0">
                {row.label}
                {row.lastResort ? (
                  <span className="ml-1 text-amber-700">· last</span>
                ) : null}
              </span>
              <span className="font-mono flex-shrink-0 text-gray-700">
                {pct}%
                <span className="text-gray-400 ml-1">{twh} TWh</span>
              </span>
            </li>
          );
        }

        if (editable && row.techId && typeof onChangeTech === 'function') {
          const draftVal =
            draftByTechId?.[row.techId] ??
            (Number.isFinite(row.capacityGw) ? String(row.capacityGw) : '');
          const isDirty = dirtyTechIds?.has?.(row.techId);
          return (
            <li
              key={key}
              className="flex items-center justify-between gap-2 text-[11px] text-gray-600"
            >
              <span className="truncate min-w-0" title={row.label}>
                {row.label}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={draftVal}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === '' || DECIMAL_2_REGEX.test(next)) {
                    onChangeTech(row.techId, next);
                  }
                }}
                onFocus={selectInputValueOnFocus}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && onEnter) {
                    e.preventDefault();
                    onEnter();
                  }
                }}
                className={`w-[4.75rem] input-field-figma py-1 text-[11px] font-mono text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  isDirty ? dirtyInputClass : ''
                }`}
                aria-label={`${row.label} capacity GW`}
              />
            </li>
          );
        }

        const gw = Number.isFinite(row.capacityGw) ? row.capacityGw.toFixed(2) : '—';
        return (
          <li
            key={key}
            className="flex items-center justify-between gap-2 text-[11px] text-gray-600"
          >
            <span className="truncate min-w-0">{row.label}</span>
            <span className="font-mono flex-shrink-0 text-gray-700">{gw} GW</span>
          </li>
        );
      })}
    </ul>
  );
}
